// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

const getInitialSettings = () => ({
    theme: localStorage.getItem('cachedTheme') || 'light',
    animationsEnabled: true,
    inventoryEnabled: true,
    notifications: {
        assignment: true,
        requestAssignment: true
    },
    ticketDashboard: {
        lists: [{
            id: 'default-open-tickets',
            title: 'All Open Tickets',
            columns: ['subject', 'client', 'priority', 'status', 'assignedTo'],
            filters: {
                status: ['Open', 'In Progress', 'Pending', 'Waiting On Client', 'Waiting On Parts'],
                priority: [],
                departments: []
            }
        }],
        layouts: {
            lg: [{ i: 'default-open-tickets', x: 0, y: 0, w: 12, h: 25, minW: 6, minH: 15 }]
        },
        smartLists: [{
            id: 'default-open-tickets-smart',
            name: 'Open Tickets',
            filters: {
                communities: [],
                statuses: ['Open', 'In Progress', 'Pending', 'Waiting On Client', 'Waiting On Parts'],
                priorities: []
            }
        }]
    },
    technicianDashboard: {
        lists: [{
            id: 'default-active-techs',
            title: 'All Active Technicians',
            columns: ['name', 'email', 'phone', 'status', 'specializations'],
            filters: {
                status: ['Active'],
                skillLevel: [],
                specializations: []
            }
        }],
        layouts: {
            lg: [{ i: 'default-active-techs', x: 0, y: 0, w: 12, h: 25, minW: 6, minH: 15 }]
        }
    }
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [settings, setSettings] = useState(getInitialSettings());
    const [isSettingsLoading, setIsSettingsLoading] = useState(true);

    const fetchAllSettings = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) { setIsSettingsLoading(false); return; }

        setIsSettingsLoading(true);
        try {
            const userSettingsRes = await fetch('/api/settings', { headers: { 'Authorization': `Bearer ${token}` } });
            
            const userSettings = userSettingsRes.ok ? await userSettingsRes.json() : {};
            const systemSettings = {};
            
            const defaults = getInitialSettings();

            const upgradeDashboard = (userDashboard, defaultDashboard) => {
                if (
                    !userDashboard ||
                    typeof userDashboard.lists === 'undefined' ||
                    userDashboard.lists.length === 0
                ) {
                    return defaultDashboard;
                }
                
                return {
                    ...defaultDashboard,
                    ...userDashboard
                };
            };
            
            const combinedSettings = {
                ...defaults,
                ...systemSettings,
                ...userSettings,
                notifications: {
                    ...defaults.notifications,
                    ...(userSettings.notifications || {})
                },
                ticketDashboard: upgradeDashboard(userSettings.ticketDashboard, defaults.ticketDashboard),
                technicianDashboard: upgradeDashboard(userSettings.technicianDashboard, defaults.technicianDashboard),
            };

            setSettings(combinedSettings);

            if (combinedSettings.theme) localStorage.setItem('cachedTheme', combinedSettings.theme);

        } catch (error) { 
            console.error("Failed to fetch settings:", error); 
        } finally { 
            setIsSettingsLoading(false); 
        }
    }, []);

    const updateUserFromToken = useCallback((token) => {
        const decodedToken = jwtDecode(token);
        setUser({ id: decodedToken.id, name: decodedToken.name, roles: decodedToken.roles, departments: decodedToken.departments, profilePictureUrl: decodedToken.profilePictureUrl });
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp * 1000 > Date.now()) {
                    updateUserFromToken(token);
                    fetchAllSettings();
                } else { logout(); }
            } catch (error) { console.error("Invalid token found:", error); logout(); }
        } else { setIsSettingsLoading(false); }
    }, [fetchAllSettings, updateUserFromToken]);

    const login = useCallback((token) => {
        localStorage.setItem('token', token);
        updateUserFromToken(token);
        fetchAllSettings();
    }, [fetchAllSettings, updateUserFromToken]);

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        setIsSettingsLoading(false);
    };
    
    const updateSettings = useCallback(async (newSettings) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        let finalPayload = {};

        setSettings(prev => {
            const merged = { ...prev };

            if (newSettings.ticketDashboard) {
                merged.ticketDashboard = { ...prev.ticketDashboard, ...newSettings.ticketDashboard };
            }
            if (newSettings.technicianDashboard) {
                merged.technicianDashboard = { ...prev.technicianDashboard, ...newSettings.technicianDashboard };
            }
            if (newSettings.notifications) {
                merged.notifications = { ...prev.notifications, ...newSettings.notifications };
            }
            // Direct top-level settings
            ['theme', 'animationsEnabled', 'inventoryEnabled'].forEach(key => {
                if (typeof newSettings[key] !== 'undefined') {
                    merged[key] = newSettings[key];
                }
            });
            
            finalPayload = { ...newSettings }; // Send only the changed part
            
            return merged;
        });
        
        if (newSettings.theme) localStorage.setItem('cachedTheme', newSettings.theme);
        
        try {
            await fetch('/api/settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(finalPayload)
            });
        } catch (error) {
            console.error("Error saving user settings:", error);
        }
    }, []);
    
    const applyLiveSettingsUpdate = useCallback((update) => {
        setSettings(prev => ({ ...prev, ...update }));
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, settings, updateSettings, isSettingsLoading, applyLiveSettingsUpdate }}>
            {children}
        </AuthContext.Provider>
    );
};